// The queries behind the privacy screens (ADR tenant-export-deletion-and-consent) and the notice
// banner (ADR in-app-notices-from-staff). Keys carry the tenant id, so nothing cached for one
// workspace or person is shown to another.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSession } from "../auth/session.js";
import { useApi } from "../http/clients.js";
export const privacyKeys = {
    me: (tenantId) => ['identity', tenantId, 'me'],
    workspace: (tenantId) => ['platform', tenantId, 'workspace'],
    export: (tenantId, exportId) => ['platform', tenantId, 'export', exportId],
    notices: (tenantId) => ['engagement', tenantId, 'notices'],
};
function useTenantId() {
    return useSession().principal?.tenantId ?? null;
}
/** `GET /me`: the caller, the documents still due and how they chose to receive notices. */
export function useMe() {
    const api = useApi();
    const tenantId = useTenantId();
    return useQuery({
        queryKey: privacyKeys.me(tenantId),
        queryFn: async () => (await api.identity.Self_get()).data,
        enabled: Boolean(tenantId),
        staleTime: 60_000,
    });
}
/** `POST /me/consents`: accept the documents shown (and the email notice choice). */
export function useAcceptDocuments() {
    const api = useApi();
    const tenantId = useTenantId();
    const client = useQueryClient();
    return useMutation({
        mutationFn: async (body) => (await api.identity.Self_consent({ body })).data,
        onSuccess: (me) => client.setQueryData(privacyKeys.me(tenantId), me),
    });
}
/** `GET /workspace`: the caller's workspace, including a deletion that is scheduled. */
export function useWorkspace() {
    const api = useApi();
    const tenantId = useTenantId();
    return useQuery({
        queryKey: privacyKeys.workspace(tenantId),
        queryFn: async () => (await api.platform.Workspaces_get()).data,
        enabled: Boolean(tenantId),
    });
}
export function useRequestDeletion() {
    const api = useApi();
    const tenantId = useTenantId();
    const client = useQueryClient();
    return useMutation({
        mutationFn: async () => (await api.platform.WorkspaceDeletions_request()).data,
        onSuccess: () => client.invalidateQueries({ queryKey: privacyKeys.workspace(tenantId) }),
    });
}
export function useCancelDeletion() {
    const api = useApi();
    const tenantId = useTenantId();
    const client = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            await api.platform.WorkspaceDeletions_cancel();
        },
        onSuccess: () => client.invalidateQueries({ queryKey: privacyKeys.workspace(tenantId) }),
    });
}
export function useRequestExport() {
    const api = useApi();
    return useMutation({
        mutationFn: async () => (await api.platform.WorkspaceExports_create()).data,
    });
}
/** How often a pending export is asked about. */
export const EXPORT_POLL_MS = 3_000;
/** `GET /workspace/exports/{id}`, polled while the copy is being prepared. */
export function useExportStatus(exportId, pollMs = EXPORT_POLL_MS) {
    const api = useApi();
    const tenantId = useTenantId();
    return useQuery({
        queryKey: privacyKeys.export(tenantId, exportId),
        queryFn: async () => (await api.platform.WorkspaceExports_get({ params: { exportId: exportId ?? '' } })).data,
        enabled: Boolean(tenantId && exportId),
        retry: false,
        refetchInterval: (query) => (query.state.data?.status === 'pending' ? pollMs : false),
    });
}
/** A fresh download link: the one shown with the status lives only a few minutes. */
export function useFreshExportLink() {
    const api = useApi();
    return useMutation({
        mutationFn: async (exportId) => (await api.platform.WorkspaceExports_get({ params: { exportId } })).data,
    });
}
/** `GET /notices`: the notices from us that concern the caller, acknowledged ones included. */
export function useNotices() {
    const api = useApi();
    const tenantId = useTenantId();
    return useQuery({
        queryKey: privacyKeys.notices(tenantId),
        queryFn: async () => (await api.engagement.Notices_list()).items,
        enabled: Boolean(tenantId),
        staleTime: 30_000,
        // A banner that arrives mid-session is still seen: ask again now and then, and on focus.
        refetchInterval: 5 * 60_000,
        refetchOnWindowFocus: true,
    });
}
export function useAcknowledgeNotice() {
    const api = useApi();
    const tenantId = useTenantId();
    const client = useQueryClient();
    return useMutation({
        mutationFn: async (noticeId) => (await api.engagement.Notices_acknowledge({ params: { noticeId } })).data,
        onSuccess: () => client.invalidateQueries({ queryKey: privacyKeys.notices(tenantId) }),
    });
}
//# sourceMappingURL=queries.js.map